import { CustomerModel } from '../../models/customer.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from './auth.reducer';
import * as fromApp from '../../store/app.reducer';
import * as AuthActions from './auth.actions';
import * as SharedActions from '../../shared-store/shared.actions';
import { environment } from '../../../environments/environment';
import { catchError, switchMap, withLatestFrom, map, timeout, delay, retry, finalize, exhaustMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { EmployeeModel } from '../../models/employee.model';
import { TranslateService } from '@ngx-translate/core';
import { BillingInformationModel } from '../../models/billingInformation.model';
import { ErrorModel } from '../../models/error.model';
import { CryptoService } from '../../shared/services/crypto.service';
import { ToasterService } from '../../service/toaster.service';
import { CapitalizePipe } from '../../shared/pipes/capitalize.pipe';
import { LoadingService } from '../../service/loading-service.service';
import { PosthogService } from '../../service/posthog.service';

@Injectable()
export class AuthEffects {
    constructor(
        private actions$: Actions,
        private http: HttpClient,
        private router: Router,
        private store: Store<fromApp.AppState>,
        private translate: TranslateService,
        private toasterService: ToasterService,
        private cryptoService: CryptoService,
        private capitalizePipe: CapitalizePipe,
        private loadingService: LoadingService,
        private posthogService: PosthogService
    ) {
    }

    init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROOT_EFFECTS_INIT),
            delay(100),
            map(() => {
                const token = localStorage.getItem('jwt');
                const tokenExpiration = localStorage.getItem('tokenExpiration');

                if (token) {
                    if (tokenExpiration) {
                        const expirationTime = parseInt(tokenExpiration);
                        const currentTime = new Date().getTime();

                        if (currentTime > expirationTime) {
                            localStorage.removeItem('jwt');
                            localStorage.removeItem('tokenExpiration');
                            return new AuthActions.LoginFailed();
                        }
                    }
                    return new AuthActions.Autologin();
                }
                return new AuthActions.LoginFailed();
            })
        )
    );

    sendRegistrationCode = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.SIGNUP_START),
            switchMap((storeData: AuthActions.SignupStart) => {
                return this.http.post(environment.apiUrl + '/auth/employees/send-registration-code', {
                    email: storeData.payload.email
                })
                    .pipe(
                        switchMap(() => {
                            this.router.navigate(['/register/security-code']);
                            return of(new AuthActions.SignupCodeSent());
                        }),
                        catchError(error => {
                            this.toasterService.showError(error.error.error);
                            return of(new AuthActions.LoginFailed());
                        }),
                    )
            }),
        );
    });

    employeeRegister = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.REGISTER_START),
            withLatestFrom(this.store.select(state => state.auth)),
            switchMap(([action, state]: [AuthActions.RegisterStart, State]) => {
                let data: any = {};
                data.email = action.payload.email;
                data.password = action.payload.password;
                data.confirmPassword = action.payload.confirmPassword;
                this.loadingService.showLoader();
                return this.http.post(environment.apiUrl + '/auth/employees/employee-register', data)
                    .pipe(
                        switchMap((resData: { data: EmployeeModel, jwt: { accessToken: string, expiresIn: number, tokenType: string } }) => {
                            this.handleSuccessfulAuth(resData);
                            this.toasterService.showSuccess('auth.register.register-success');
                            // OLD FLOW (kept as requested):
                            // if (resData.data?.customers?.length > 0) {
                            //     this.router.navigate(['/components/assignments']);
                            // } else {
                            //     this.router.navigate(['/components/on-boarding']);
                            // }

                            // NEW FLOW:
                            // 1) If employee already has customers, continue to assignments.
                            // 2) If first-time employee, auto-create a company from employee profile
                            // and then redirect directly to assignments.
                            const baseActions = [
                                new AuthActions.RegisterSuccess({ employee: resData.data, jwt: resData.jwt }),
                                new AuthActions.ClearInviteToken()
                            ];

                            if (resData.data?.customers?.length > 0) {
                                this.router.navigate(['/components/assignments']);
                                return of(baseActions);
                            }

                            return this.createDefaultCompanyForFirstTimeEmployee(resData.data).pipe(
                                map((customer: CustomerModel) => {
                                    localStorage.setItem('customer', customer.id.toString());
                                    this.router.navigate(['/components/assignments']);
                                    return [
                                        ...baseActions,
                                        new AuthActions.CreateCustomerSuccess({ customer }),
                                        new AuthActions.SwitchCompanyAccount({ customer })
                                    ];
                                }),
                                catchError(error => {
                                    // If auto-create fails, still allow login and send user to assignments.
                                    // They can create/update company later from company settings.
                                    this.toasterService.showError(error?.error?.error || 'Could not auto create company');
                                    this.router.navigate(['/components/assignments']);
                                    return of(baseActions);
                                })
                            );
                        }),
                        switchMap(actions => actions),
                        catchError(error => {
                            this.toasterService.showError(error.error?.error || 'Registration failed');
                            return of(new AuthActions.RegisterFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    private createDefaultCompanyForFirstTimeEmployee(employee: EmployeeModel) {
        const formData = new FormData();
        const defaultCompanyName = this.getDefaultCompanyName(employee);
        const contactPerson = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || defaultCompanyName;
        const generatedOrgNumber = this.getGeneratedOrganisationNumber();

        formData.append('idNumber', generatedOrgNumber);
        formData.append('companyName', defaultCompanyName);
        formData.append('contactPerson', contactPerson);
        formData.append('email', employee?.email || '');
        formData.append('phone', employee?.phone || '');
        formData.append('address', '');
        formData.append('city', '');
        formData.append('biography', '');
        formData.append('customerWebsite', '');
        // Backend flag to identify customer auto-created at first-time registration.
        formData.append('is_auto_created_from_registration', 'true');

        return this.http
            .post<{ data: CustomerModel }>(environment.apiUrl + '/customers', formData)
            .pipe(map(res => res.data));
    }

    private getDefaultCompanyName(employee: EmployeeModel): string {
        const fullName = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim();
        if (fullName) {
            return fullName;
        }
        const emailPrefix = (employee?.email || '').split('@')[0]?.trim();
        return emailPrefix || 'New Company';
    }

    private getGeneratedOrganisationNumber(): string {
        const digits = Date.now().toString();
        return digits.slice(-10).padStart(10, '0');
    }

    loginLink = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.LOGIN_LINK),
            switchMap((storeData: AuthActions.SendLoginLink) => {
                return this.http.post(environment.apiUrl + '/auth/employees/send-login-email', {
                    email: storeData.payload.email,
                })
                    .pipe(
                        switchMap(() => {
                            this.router.navigate(['/login-email-step2']);
                            this.toasterService.showSuccess('Link send successfully! Check your email');
                            return of(new AuthActions.LoginLinkSent());
                        }),
                        catchError(error => {
                            this.toasterService.showError('Failed to send login link! Please try again later');
                            return of(new AuthActions.LoginFailed());
                        }),
                    )
            }),
        );
    });

    login = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.LOGIN_START),
            withLatestFrom(this.store.select(state => state.auth)),
            switchMap(([action, state]: [AuthActions.LoginStart, State]) => {
                let data: any = {};
                data.email = action.payload.email;
                data.password = action.payload.password;
                this.loadingService.showLoader();
                return this.http.post(environment.apiUrl + '/auth/employees/employee-login', data)
                    .pipe(
                        switchMap((resData: { data: EmployeeModel, jwt: { accessToken: string, expiresIn: number, tokenType: string } }) => {
                            if (!resData.jwt?.accessToken) {
                                throw new Error('No valid token received from server');
                            }
                            this.handleSuccessfulAuth(resData);
                            this.toasterService.showSuccess('auth.login.login-success');
                            if (resData.data?.customers?.length > 0) {
                                this.router.navigate(['/components/assignments']);
                            } else {
                                this.router.navigate(['/components/on-boarding']);
                            }

                            return [
                                new AuthActions.LoginSuccess({ employee: resData.data, jwt: resData.jwt }),
                                new AuthActions.ClearInviteToken(),
                                new SharedActions.SendFirebaseTokenToServer(),
                            ];
                        }),
                        catchError(error => {
                            localStorage.removeItem('jwt');
                            localStorage.removeItem('tokenExpiration');
                            this.toasterService.showError(error.error?.error || 'Login failed');
                            return of(new AuthActions.LoginFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    autologin = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.AUTOLOGIN),
            exhaustMap(() => {
                const token = localStorage.getItem('jwt');
                if (!token) {
                    return of(new AuthActions.LoginFailed());
                }

                return this.http
                    .get(environment.apiUrl + '/auth/employees/autologin', {
                        headers: {
                            Authorization: 'Bearer ' + token,
                        },
                    })
                    .pipe(
                        timeout(30000),
                        retry({ count: 1, delay: 2000 }),
                        switchMap(
                            (
                                resData: {
                                    data: EmployeeModel;
                                    jwt: { accessToken: string; expiresIn: number; tokenType: string };
                                }
                            ) => {
                                if (resData.jwt?.accessToken) {
                                    this.handleSuccessfulAuth(resData);
                                }
                                this.setLocaleLanguage(resData.data);

                                // Set initial company if not already set
                                if (!localStorage.getItem('customer') && resData.data?.customers?.length > 0) {
                                    const firstCustomer = resData.data.customers[0];
                                    localStorage.setItem('customer', firstCustomer.id.toString());
                                    return [
                                        new AuthActions.LoginSuccess({
                                            jwt: resData.jwt || { accessToken: token },
                                            employee: resData.data,
                                        }),
                                        new AuthActions.SwitchCompanyAccount({ customer: firstCustomer }),
                                        new SharedActions.SendFirebaseTokenToServer(),
                                    ];
                                }

                                return [
                                    new AuthActions.LoginSuccess({
                                        jwt: resData.jwt || { accessToken: token },
                                        employee: resData.data,
                                    }),
                                    new SharedActions.SendFirebaseTokenToServer(),
                                ];
                            }
                        ),
                        catchError((error) => {
                            console.error('Autologin error:', error);

                            if (error.status === 401 || error.status === 403) {
                                localStorage.removeItem('jwt');
                                localStorage.removeItem('tokenExpiration');
                                this.router.navigate(['/login']);
                                return of(new AuthActions.LoginFailed());
                            }

                            if (error.name === 'TimeoutError' || error.status === 0 || error.status >= 500) {
                                console.warn('Network error during autologin, keeping session');
                                return of(
                                    new AuthActions.LoginSuccess({
                                        jwt: { accessToken: token },
                                        employee: null,
                                    })
                                );
                            }

                            return of(new AuthActions.LoginFailed());
                        })
                    );
            })
        );
    });

    forgotPassword = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.FORGOT_PASSWORD_START),
            switchMap((storeData: AuthActions.ForgotPasswordStart) => {
                this.loadingService.showLoader();
                return this.http.post(environment.apiUrl + '/auth/employees/send-forgot-password-link', {
                    email: storeData.payload.email
                })
                    .pipe(
                        switchMap(() => {
                            const encryptedEmail = this.cryptoService.encryptData(storeData.payload.email);
                            this.router.navigate(['/reset-password-link'], {
                                queryParams: { email: encryptedEmail }
                            });
                            this.toasterService.showSuccess('auth.forgot-password.link-sent');
                            return of(new AuthActions.ForgotPasswordSuccess({ message: this.translate.instant('auth.forgot-password.link-sent') }));
                        }),
                        catchError(error => {
                            this.toasterService.showError(error.error?.error || 'Failed to send reset link');
                            return of(new AuthActions.ForgotPasswordFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    resetPassword = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.RESET_PASSWORD_START),
            switchMap((storeData: AuthActions.ResetPasswordStart) => {
                this.loadingService.showLoader();
                return this.http.post(environment.apiUrl + '/auth/employees/reset-password', {
                    token: storeData.payload.token,
                    password: storeData.payload.password,
                    confirmPassword: storeData.payload.confirmPassword
                })
                    .pipe(
                        switchMap(() => {
                            this.router.navigate(['/password-reset-success']);
                            this.toasterService.showSuccess('auth.reset-password.password-reset');
                            return of(new AuthActions.ResetPasswordSuccess({ message: this.translate.instant('auth.reset-password.password-reset') }));
                        }),
                        catchError(error => {
                            this.toasterService.showError(error.error?.error || 'Password reset failed');
                            return of(new AuthActions.ResetPasswordFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    changePassword = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.CHANGE_PASSWORD),
            switchMap((storeData: AuthActions.ChangePassword) => {
                this.loadingService.showLoader();
                return this.http.put(environment.apiUrl + '/employees/' + storeData.payload.employeeId + '/update-password', {
                    oldPassword: storeData.payload.oldPassword,
                    password: storeData.payload.password,
                    password_confirmation: storeData.payload.confirmPassword
                })
                    .pipe(
                        switchMap((resData: { data: EmployeeModel }) => {
                            this.toasterService.showSuccess('profile.change-password-msg');
                            // 1. First disconnect realtime services and dispatch logout
                            sessionStorage.clear();
                            localStorage.clear();
                            window.location.href = '/login';
                            return of(new AuthActions.ChangePasswordSuccess({ employee: resData.data }));
                        }),
                        catchError(error => {
                            this.toasterService.showError(error.error?.message || 'Password change failed');
                            return of(new AuthActions.ChangePasswordFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    updateProfile = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.UPDATE_PROFILE),
            switchMap((storeData: AuthActions.UpdateProfile) => {
                const formData = new FormData();
                // if (storeData.payload.firstName)
                //     formData.append('first_name', storeData.payload.firstName ||null);
                if (storeData.payload.lastName)
                    formData.append('last_name', storeData.payload.lastName);
                if (storeData.payload.email)
                    formData.append('email', storeData.payload.email);
                if (storeData.payload.locale)
                    formData.append('locale', storeData.payload.locale);
                if (storeData.payload.avatar) {
                    formData.append('avatar', storeData.payload.avatar);
                }
                if (storeData.payload.firstName !== undefined) {
                    formData.append('first_name', storeData.payload.firstName ?? '');
                }
                if (storeData.payload.phone !== undefined) {
                    formData.append('phone', storeData.payload.phone ?? '');
                }

                this.loadingService.showLoader();
                return this.http.post(environment.apiUrl + '/employees/' + storeData.payload.employeeId, formData)
                    .pipe(
                        switchMap((resData: { data: EmployeeModel }) => {
                            this.setLocaleLanguage(resData.data);
                            this.toasterService.showSuccess('profile.profile-updated');
                            return of(new AuthActions.UpdateProfileSuccess({ employee: resData.data }));
                        }),
                        catchError(error => {
                            this.toasterService.showError(error.error?.error || 'Profile update failed');
                            return of(new AuthActions.UpdateProfileFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    verifyEmail = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.VERIFY_NEW_EMAIL),
            switchMap((storeData: AuthActions.VerifyNewEmail) => {
                this.loadingService.showLoader();
                return this.http.post(environment.apiUrl + '/employees/' + storeData.payload.employeeId + '/request-email-change', {
                    email: storeData.payload.email,
                })
                    .pipe(
                        switchMap(() => {
                            this.toasterService.showSuccess('Check your email to confirm the change!');
                            return of(new AuthActions.VerifyNewEmailSuccess());
                        }),
                        catchError(error => {
                            this.toasterService.showError(error.error?.error || 'Email verification failed');
                            return of(new AuthActions.UpdateProfileFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    updateEmail = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.UPDATE_EMAIL),
            switchMap((storeData: AuthActions.UpdateEmail) => {
                this.loadingService.showLoader();
                return this.http.put(environment.apiUrl + '/employees/' + storeData.payload.token + '/update-email', {
                    password: storeData.payload.password,
                })
                    .pipe(
                        switchMap((resData: { data: EmployeeModel }) => {
                            this.toasterService.showSuccess('profile.email-changed');
                            this.router.navigate(['/components/profile'])
                            return of(new AuthActions.UpdateProfileSuccess({ employee: resData.data }));
                        }),
                        catchError(error => {
                            this.toasterService.showError(error.error?.error || 'Email update failed');
                            return of(new AuthActions.UpdateProfileFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    createCustomer = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.CREATE_CUSTOMER),
            switchMap((action: AuthActions.CreateCustomer) => {
                this.loadingService.showLoader();
                return this.http.post(environment.apiUrl + '/customers', action.payload)
                    .pipe(
                        switchMap((resData: { data: CustomerModel }) => {
                            this.toasterService.showSuccess('company.company-created');

                            // Set the newly created customer as active in localStorage
                            localStorage.setItem('customer', resData.data.id.toString());

                            return [
                                new AuthActions.CreateCustomerSuccess({
                                    customer: resData.data,
                                }),
                                new AuthActions.SwitchCompanyAccount({ customer: resData.data })
                            ];
                        }),
                        catchError((httpErrorResponse: HttpErrorResponse) => {
                            let error = httpErrorResponse.error as ErrorModel;
                            this.toasterService.showError(error.error || 'Customer creation failed');
                            return of(new AuthActions.CreateCustomerFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    updateCustomer = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.UPDATE_CUSTOMER),
            switchMap((storeData: AuthActions.UpdateCustomer) => {
                let data: any = {};
                if (storeData.payload.inviteToken)
                    data.inviteToken = storeData.payload.inviteToken;

                return this.http.put(environment.apiUrl + '/customers/' + storeData.payload.customerId, data)
                    .pipe(
                        switchMap((resData: { data: CustomerModel }) => {
                            if (resData.data.inviteToken) {
                                this.toasterService.showSuccess('company.invite.link-generated');
                            } else {
                                this.toasterService.showSuccess('company.invite.link-deactivated');
                            }
                            return of(new AuthActions.UpdateCustomerSuccess({
                                customer: resData.data,
                            }));
                        }),
                        catchError(error => {
                            this.toasterService.showError('company.invite.link-generated-failed');
                            return of(new AuthActions.UpdateCustomerFailed());
                        }),
                    )
            }),
        );
    });

    updateCustomerOrganisationNumber = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.UPDATE_CUSTOMER_ORGANISATION_NUMBER),
            switchMap((action: AuthActions.UpdateCustomerOrganisationNumber) => {
                const url = `${environment.apiUrl}/customers/${action.payload.customerId}/organisation-number`;
                const body: any = { idNumber: action.payload.organisationNumber };
                if (typeof action.payload.isAutoCreatedFromRegistration === 'boolean') {
                    body.isAutoCreatedFromRegistration = action.payload.isAutoCreatedFromRegistration;
                }
                // Pass extended customer details whenever they are provided.
                if (action.payload.companyName !== undefined) body.companyName = action.payload.companyName;
                if (action.payload.contactPerson !== undefined) body.contactPerson = action.payload.contactPerson;
                if (action.payload.email !== undefined) body.email = action.payload.email;
                if (action.payload.phone !== undefined) body.phone = action.payload.phone;
                if (action.payload.address !== undefined) body.address = action.payload.address;
                if (action.payload.city !== undefined) body.city = action.payload.city;
                if (action.payload.country !== undefined) body.country = action.payload.country;
                if (action.payload.biography !== undefined) body.biography = action.payload.biography;
                if (action.payload.customerWebsite !== undefined) body.customerWebsite = action.payload.customerWebsite;
                return this.http.put<{ data: CustomerModel }>(url, body).pipe(
                    map(resData => new AuthActions.UpdateCustomerOrganisationNumberSuccess({ customer: resData.data })),
                    catchError(() => {
                        this.toasterService.showError('Failed to update organisation number');
                        return of(new AuthActions.UpdateCustomerOrganisationNumberFailed());
                    })
                );
            })
        )
    );

    updateBilling = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.UPDATE_BILLING_INFO),
            switchMap((action: AuthActions.UpdateBillingInfo) => {
                return this.http.put(environment.apiUrl + `/customers/${action.payload.customerId}/billing-information/${action.payload.billingId}`,
                    action.payload.data)
                    .pipe(
                        switchMap((resData: { data: BillingInformationModel }) => {
                            this.toasterService.showSuccess('Billing information is successfully updated');
                            return of(new AuthActions.UpdateBillingInfoSuccess({ billing: resData.data }));
                        }),
                        catchError(error => {
                            this.toasterService.showError('Failed to update billing information');
                            return of(new AuthActions.UpdateBillingInfoFailed());
                        }),
                    )
            }),
        );
    });

    createBilling = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.CREATE_BILLING_INFO),
            switchMap((action: AuthActions.CreateBillingInfo) => {
                return this.http.post(environment.apiUrl + `/customers/${action.payload.customerId}/billing-information`, {
                    customerId: action.payload.customerId,
                    companyName: action.payload.companyName,
                    companyNumber: action.payload.companyNumber,
                    address: action.payload.address,
                    addressSecond: action.payload.addressSecond,
                    postalCode: action.payload.postalCode,
                    city: action.payload.city,
                    contactName: action.payload.contactName,
                    phone: action.payload.phone,
                    email: action.payload.email
                })
                    .pipe(
                        switchMap((resData: { data: BillingInformationModel }) => {
                            return of(new AuthActions.CreateBillingInfoSuccess({ billing: resData.data }));
                        }),
                        catchError(error => {
                            this.toasterService.showError(error.error?.error || 'Failed to create billing information');
                            return of(new AuthActions.CreateBillingInfoFailed());
                        }),
                    )
            }),
        );
    });

    storeJobPlaceBillingInfo = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.STORE_JOB_PLACE_BILLING_INFO),
            switchMap((action: AuthActions.StoreJobPlaceBillingInfo) => {
                const url = `${environment.apiUrl}/customers/${action.payload.customerId}/billing-information/job-place`;
                const body = {
                    job_place_id: action.payload.jobPlaceId,
                    payment_method: action.payload.payment_method,
                    billing_reference: action.payload.billing_reference,
                    invoice_time: action.payload.invoice_time,
                    invoice_email: action.payload.invoice_email
                };
                return this.http.post<{ data?: any }>(url, body).pipe(
                    map(() => new AuthActions.StoreJobPlaceBillingInfoSuccess()),
                    catchError(() => {
                        this.toasterService.showError('Failed to store job place billing information');
                        return of(new AuthActions.StoreJobPlaceBillingInfoFailed());
                    })
                );
            }),
        );
    });

    bankIdAuth = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.BANKID_AUTH_START),
            withLatestFrom(this.store.select(state => state.auth)),
            switchMap(([action, authState]) => {
                const token = authState.jwt ? authState.jwt.accessToken : localStorage.getItem('jwt');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};

                return this.http.get(
                    environment.apiUrl + '/bankid/auth',
                    {
                        headers
                    }).pipe(
                        map((resData: any) => {
                            return new AuthActions.BankIdAuthSuccess({
                                orderRef: resData.orderRef,
                                qrStartToken: resData.qrStartToken,
                                qrStartSecret: resData.qrStartSecret,
                                qrAutoStartToken: resData.qrAutoStartToken
                            });
                        }),
                        catchError(error => {
                            console.error('BankID auth error:', error);
                            const errorMessage = error.error?.error || 'BankID authentication failed. Please try again.';
                            this.toasterService.showError(errorMessage);
                            return of(new AuthActions.BankIdAuthFail(errorMessage));
                        })
                    );
            })
        );
    });

    bankIdCollectStatusPolling = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.BANKID_AUTH_SUCCESS),
            withLatestFrom(this.store.select(state => state.auth)),
            switchMap(([authSuccessAction, authState]: [AuthActions.BankIdAuthSuccess, State]) => {
                const token = authState.jwt ? authState.jwt.accessToken : localStorage.getItem('jwt');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};

                const orderRef = authSuccessAction.payload.orderRef;
                if (!orderRef) {
                    return of(new AuthActions.BankIdCollectFail('BankID order reference is missing.'));
                }

                return this.http.post(environment.apiUrl + '/bankid/collect',
                    { orderRef: orderRef },
                    { headers: headers }
                ).pipe(
                    // timeout(10000),
                    switchMap((response: any) => {
                        if (response.status === 'complete') {
                            return of(new AuthActions.BankIdCollectComplete({
                                status: response.status,
                                userDetails: {
                                    personalNumber: response.user.personalNumber,
                                    firstName: this.capitalizePipe.transform(response.user.givenName) || '',
                                    lastName: this.capitalizePipe.transform(response.user.surname) || '',
                                    address: response.user.address || '',
                                    city: response.user.city || '',
                                    country: response.user.country || '',
                                    email: response.user.email || '',
                                    phone: response.user.phone || ''
                                }
                            }));
                        } else if (response.status === 'failed') {
                            const errorMessage = response.hintCode || response.message || 'BankID verification failed.';
                            return of(new AuthActions.BankIdCollectFail(errorMessage));
                        } else {
                            // Still pending or outstanding transaction
                            return of(new AuthActions.BankIdCollectProgress(response.status));
                        }
                    }),
                    catchError(error => {
                        console.error('BankID collect error:', error);
                        if (error.name === 'TimeoutError') {
                            return of(new AuthActions.BankIdCollectProgress('pending'));
                        }

                        // Handle specific BankID error codes
                        if (error.status === 400 || error.status === 404) {
                            const errorMessage = error.error?.message || error.error?.hintCode || 'BankID transaction not found or expired';
                            return of(new AuthActions.BankIdCollectFail(errorMessage));
                        }

                        // For other errors, just continue polling
                        console.warn('BankID collect error, continuing polling:', error);
                        return of(new AuthActions.BankIdCollectProgress('pending'));
                    })
                );
            })
        );
    });

    /** Client-side session duration when server omits expiresIn (10 years) */
    private static readonly SESSION_MAX_DAYS = 10 * 365;

    // Helper method to handle successful authentication
    public handleSuccessfulAuth(resData: { data: EmployeeModel, jwt: { accessToken: string, expiresIn: number, tokenType: string } }): void {
        localStorage.setItem('jwt', resData.jwt.accessToken);
        if (resData.jwt.expiresIn) {
            // Backend sends expiresIn in minutes (e.g. 5256000 = 10 years)
            const expirationTime = new Date().getTime() + resData.jwt.expiresIn * 60 * 1000;
            localStorage.setItem('tokenExpiration', expirationTime.toString());
        } else {
            const tenYearsMs = AuthEffects.SESSION_MAX_DAYS * 24 * 60 * 60 * 1000;
            localStorage.setItem('tokenExpiration', (new Date().getTime() + tenYearsMs).toString());
        }
        this.setLocaleLanguage(resData.data);

        // Identify user in PostHog
        if (resData.data) {
            this.posthogService.identifyUser({
                id: resData.data.id,
                firstName: resData.data.firstName || '',
                lastName: resData.data.lastName || '',
                email: resData.data.email || ''
            });
        }
    }

    public setLocaleLanguage(employee: EmployeeModel) {
        if (employee && employee.locale) {
            localStorage.setItem('locale', employee.locale);
            this.translate.setDefaultLang('sv');
            this.translate.use(employee.locale);
        }
    }
}
